import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Button, Descriptions, Form, Space, Steps } from 'antd';
import type { FormInstance } from 'antd';
import type { AllySummary, CreateLocationBody } from '@goelectric/shared-types';
import { useAllies } from '@/features/aliados/hooks/useAllies';
import { allyDisplayName } from '@/features/aliados/utils/format';
import { locationApi } from '../api/locationApi';
import { LOCATION_DUPLICATE_MESSAGES } from '../utils/locationErrors';
import { ALLY_OPTIONS_LIMIT, buildCreateBody, type LocationFormState } from '../utils/locationForm';
import { LocationAddressFields } from './LocationAddressFields';
import { LocationGeoFields } from './LocationGeoFields';
import { LocationIdentityFields } from './LocationIdentityFields';
import { LocationMapPicker } from './LocationMapPicker';

export interface LocationFormProps {
  /** Recibe el cuerpo del contrato ya compuesto; la llamada a la API la hace la página. */
  onSubmit: (body: CreateLocationBody) => void;
  submitting?: boolean;
  /**
   * Instancia del `Form` para operarla desde afuera (p. ej. la página marcando el campo
   * de un error del backend). Si no se pasa, el formulario crea la suya.
   */
  form?: FormInstance<LocationFormState>;
  /**
   * Campo que el backend marcó en un error. Si pertenece a un paso que no es el que el
   * usuario está viendo, el wizard salta a ese paso para que pueda corregirlo.
   */
  errorField?: string | null;
}

/** Único lugar donde se declara qué campos pertenecen a cada paso (fuente del mapeo campo→paso). */
const STEPS: { title: string; fields: (keyof LocationFormState)[] }[] = [
  { title: 'Identificación y aliado', fields: ['name', 'allyId'] },
  {
    title: 'Dirección',
    fields: ['address', 'postalCode', 'state', 'city'],
  },
  { title: 'Geolocalización', fields: ['latitude', 'longitude'] },
  { title: 'Revisión', fields: [] },
];

const LAST_STEP = STEPS.length - 1;

/**
 * Departamento por defecto del **alta**: gran parte de la operación arranca en Santander.
 * Es un valor inicial **editable** (el admin lo cambia), no un valor fijo. Vive en el
 * `LocationForm` (los dos caminos de alta lo montan: `CreateLocationPage` y el aside del
 * wizard de Cargadores) y NO en el modal de edición, que precarga el valor existente.
 */
const DEFAULT_STATE = 'Santander';

/** Campo → índice de paso, derivado de `STEPS` (el salto por error y el resumen lo reusan). */
const FIELD_STEP = new Map<string, number>(
  STEPS.flatMap((step, index) => step.fields.map((field) => [field as string, index] as const)),
);

/**
 * Formulario de alta de Locación, en 4 pasos (US-2.2): identificación y aliado →
 * dirección → geolocalización → revisión.
 *
 * Espejo de `AllyForm`: los campos de todos los pasos están **montados**; los inactivos se
 * ocultan con CSS, así los valores persisten y el resumen reusa `STEPS`. "Siguiente" valida
 * solo el paso actual y recién ahí avanza; la revisión muestra lo que se va a enviar.
 *
 * La unicidad del nombre (TG-123) se chequea consultivamente al avanzar del paso de
 * identificación; es **fail-open**, la verdad sigue siendo el índice único de la DB y el POST.
 */
export function LocationForm({
  onSubmit,
  submitting = false,
  form: formProp,
  errorField,
}: LocationFormProps) {
  const [internalForm] = Form.useForm<LocationFormState>();
  const form = formProp ?? internalForm;
  const [current, setCurrent] = useState(0);
  // Mientras corre el chequeo consultivo de unicidad (TG-123): el botón muestra el spinner.
  const [checking, setChecking] = useState(false);
  // Un solo fetch de aliados para el selector del paso 1 y el nombre del resumen.
  const allies = useAllies({ limit: ALLY_OPTIONS_LIMIT });

  const handleFinish = (values: LocationFormState) => {
    const body = buildCreateBody(values);
    if (body) {
      onSubmit(body);
    }
  };

  // Salto declarativo al paso del campo que marcó el backend (sin refs ni handles).
  useEffect(() => {
    if (!errorField) {
      return;
    }
    const step = FIELD_STEP.get(errorField);
    if (step !== undefined) {
      setCurrent(step);
    }
  }, [errorField]);

  /**
   * Chequeo consultivo de unicidad del nombre al avanzar del paso de identificación
   * (TG-123). Devuelve `true` si el nombre está tomado y **no** se debe avanzar (ya marcó
   * el campo). Es **fail-open**: si la consulta falla (red, 500, timeout) devuelve `false`
   * y deja avanzar, porque la verdad sigue siendo el índice único de la DB y el POST final;
   * un endpoint de consulta caído no puede bloquear el alta.
   */
  const hasDuplicatesInCurrentStep = async (): Promise<boolean> => {
    if (current === 0) {
      const name = (form.getFieldValue('name') as string | undefined)?.trim();
      if (!name) {
        return false;
      }
      // TG-123: nombre y aliado viven en el mismo paso; el chequeo va acotado al aliado
      // elegido (`''`/sin valor = espacio de plataforma).
      const allyId = (form.getFieldValue('allyId') as string | undefined) || undefined;
      setChecking(true);
      try {
        const { available } = await locationApi.checkNameAvailability(name, allyId);
        if (available) {
          return false;
        }
        form.setFields([{ name: 'name', errors: [LOCATION_DUPLICATE_MESSAGES.name] }]);
        return true;
      } catch {
        return false;
      } finally {
        setChecking(false);
      }
    }

    return false;
  };

  /** Avanza solo si el paso valida y no hay duplicados; hacia atrás no se valida nada. */
  const goNext = async () => {
    try {
      await form.validateFields(STEPS[current]?.fields ?? []);
    } catch {
      // El paso no valida: antd ya muestra los errores inline.
      return;
    }
    if (await hasDuplicatesInCurrentStep()) {
      return;
    }
    setCurrent((step) => Math.min(step + 1, LAST_STEP));
  };

  // Submit final: si algún campo de un paso anterior quedó inválido (raro), salta a su paso
  // en vez de fallar en silencio con un botón que "no hace nada".
  const handleFinishFailed = (info: { errorFields: { name: (string | number)[] }[] }) => {
    const first = info.errorFields[0]?.name?.[0];
    const step = typeof first === 'string' ? FIELD_STEP.get(first) : undefined;
    if (step !== undefined) {
      setCurrent(step);
    }
  };

  return (
    <Form
      className="locaciones-form"
      form={form}
      layout="vertical"
      initialValues={{ state: DEFAULT_STATE }}
      onFinish={handleFinish}
      onFinishFailed={handleFinishFailed}
    >
      <div className="locaciones-form__header">
        <Steps
          current={current}
          size="small"
          labelPlacement="vertical"
          items={STEPS.map((step) => ({ title: step.title }))}
          style={{ marginBottom: 12 }}
        />
      </div>

      <div style={{ display: current === 0 ? undefined : 'none' }}>
        <LocationIdentityFields allies={allies.data} alliesLoading={allies.loading} />
      </div>

      <div style={{ display: current === 1 ? undefined : 'none' }}>
        <LocationAddressFields />
      </div>

      <div style={{ display: current === 2 ? undefined : 'none' }}>
        {/*
          El mapa va ARRIBA de los inputs (es un selector de precisión: primero se ubica el
          punto, después se ajustan los números). Se monta sólo en su paso: Leaflet dentro de
          un contenedor `display:none` mide 0 y queda roto. No es un campo del form (los
          valores viven en los InputNumber), así que no hace falta dejarlo montado en los
          otros pasos.
        */}
        {current === 2 && <LocationMapPicker />}
        <LocationGeoFields isEdit={false} />
      </div>

      {current === LAST_STEP && <LocationReview form={form} allies={allies.data} />}

      <Space style={{ marginTop: 8 }}>
        {current > 0 && <Button onClick={() => setCurrent((step) => step - 1)}>Anterior</Button>}
        {current < LAST_STEP && (
          <Button type="primary" onClick={goNext} loading={checking}>
            Siguiente
          </Button>
        )}
        {current === LAST_STEP && (
          <Button type="primary" htmlType="submit" loading={submitting}>
            Crear ubicación
          </Button>
        )}
      </Space>
    </Form>
  );
}

interface SummaryField {
  label: string;
  value: (values: LocationFormState) => ReactNode;
}

/**
 * Paso 4: resumen de lo que se va a enviar. Se monta solo en la revisión (no tiene campos),
 * así no re-renderiza el wizard en cada tecla. El orden y la agrupación salen de `STEPS`.
 */
function LocationReview({
  form,
  allies,
}: {
  form: FormInstance<LocationFormState>;
  allies: AllySummary[];
}) {
  const values = Form.useWatch([], form) ?? ({} as LocationFormState);
  const allyNameById = new Map(allies.map((ally) => [ally.id, allyDisplayName(ally)]));

  const summaryFields: Partial<Record<keyof LocationFormState, SummaryField>> = {
    name: { label: 'Nombre', value: (v) => v.name || '—' },
    address: { label: 'Dirección', value: (v) => v.address || '—' },
    postalCode: { label: 'Código postal', value: (v) => v.postalCode || '—' },
    city: { label: 'Municipio', value: (v) => v.city || '—' },
    state: { label: 'Departamento', value: (v) => v.state || '—' },
    latitude: { label: 'Latitud', value: (v) => (v.latitude != null ? String(v.latitude) : '—') },
    longitude: {
      label: 'Longitud',
      value: (v) => (v.longitude != null ? String(v.longitude) : '—'),
    },
    allyId: {
      label: 'Aliado',
      value: (v) => (v.allyId ? allyNameById.get(v.allyId) ?? v.allyId : 'Sin aliado'),
    },
  };

  return (
    <>
      {STEPS.slice(0, LAST_STEP).map((step) => {
        const items = step.fields.flatMap((field) => {
          const meta = summaryFields[field];
          return meta ? [{ key: field, meta }] : [];
        });
        if (items.length === 0) {
          return null;
        }
        return (
          <Descriptions
            key={step.title}
            title={step.title}
            column={1}
            size="small"
            bordered
            style={{ marginBottom: 16 }}
          >
            {items.map(({ key, meta }) => (
              <Descriptions.Item key={key} label={meta.label}>
                {meta.value(values)}
              </Descriptions.Item>
            ))}
          </Descriptions>
        );
      })}
    </>
  );
}
